/**
 * Buchhaltungsexport für österreichische Kanzleien im BMD-Importformat („Buchungen importieren“, BMD NTCS; RZL liest dieselbe
 * CSV-Datei in der Regel ebenfalls ein). Rein; die Oberfläche kodiert die Datei als Windows-1252 (winAnsiFileBytes) und lädt sie
 * herunter. Gleiche Belegauswahl wie DATEV (datevBooks): Ausgangsrechnungen an Marken und Creator, Rechnungen und Gutschriften der
 * Creator erst ab der Freigabe, nie Rechnungen eines Creators direkt an die Marke.
 *
 * Annahmen zum Format (aus der BMD-Schnittstellenbeschreibung, vor dem ersten Import mit der Kanzlei an einem Beispiel prüfen):
 * - Erste Zeile: Feldnamen; BMD ordnet die Spalten über diese Namen zu. Trennzeichen Semikolon, Dezimalkomma ohne
 *   Tausenderpunkt, Datum TT.MM.JJJJ, Zeilenende CRLF, Zeichensatz Windows-1252 (ANSI).
 * - Felder: satzart (0 = Buchung), konto (Personenkonto: Sammeldebitor bzw. Sammelkreditor), gkonto (Erlös- bzw. Aufwandskonto),
 *   belegnr (nur Ziffern, höchstens 9 Stellen: die Ziffern der Rechnungsnummer), extbelegnr (die Rechnungsnummer, wie sie auf
 *   dem Beleg steht), belegdatum, buchsymbol (AR = Ausgangsrechnung, ER = Eingangsrechnung, SO = Sonstige), buchcode
 *   (1 = Soll, 2 = Haben, aus Sicht von konto), prozent (Steuersatz), steuercode, betrag (brutto), steuer, text, zziel (Tage).
 * - Vorzeichen: betrag aus Sicht von konto (Soll positiv, Haben negativ); die Steuer steht auf der Gegenseite und hat deshalb
 *   das umgekehrte Vorzeichen. Beides ist einstellbar (taxSign), weil Kanzleien die Vorlage unterschiedlich anlegen.
 * - Steuercode (K-252): jede Buchungszeile trägt den Code ihres Steuerfalls aus der Codetabelle (BMD_DEFAULT_TAX_CODES, je
 *   Agentur überschreibbar; ein leer gespeicherter Code gilt als nicht gepflegt und fällt auf den Vorschlag zurück, nie leer):
 *   „1“ normale Umsatz- bzw. Vorsteuer 20/10/13 % (Richtung aus AR/ER); „77“ sonstige Leistung an EU-Unternehmer, Steuerschuld
 *   beim Empfänger (§ 3a Abs. 6: in Österreich nicht steuerbar, nicht in der UVA, nur Zusammenfassende Meldung); „19“ Reverse Charge beim Leistungsbezug (§ 19 Abs. 1 UStG, EU- und Drittland-
 *   Creator an die Agentur); „0“ ausdrücklich ohne Steuer für Drittland (Leistungsort Ausland, nicht steuerbar), Kleinunternehmer
 *   (§ 6 Abs. 1 Z 27, steuerfrei ohne Vorsteuerabzug, KZ 000/016 über das Sachkonto) und Verzugskosten (nicht steuerbarer
 *   Schadenersatz). Die Kennzahl der Umsatzsteuervoranmeldung hängt in BMD zusätzlich am Sachkonto; die Vorschläge vor dem
 *   ersten Import mit der Kanzlei abstimmen. Ein Inlandsfall ohne ausgewiesene Steuer (bzw. Vorsteuer einer Kleinunternehmer-
 *   Agentur) bleibt ohne Code, weil dort keine Steuer gebucht wird.
 * - Beträge in Euro (Hauswährung). Rechnungen in fremder Währung nehmen den beim Festschreiben eingefrorenen Euro-Betrag; fehlt
 *   er, bleibt die Rechnung draußen und wird gemeldet. K-308 (0274): Mit der eingefrorenen Umsatzsteuer in Euro (PDF, 0267/0269)
 *   ist die Steuer genau dieser Betrag und das Netto mit demselben Kurs umgerechnet (datevEurAmounts, wie im DATEV-Stapel).
 * - Storno: Stornorechnungen (negative Beträge) buchen mit umgekehrtem Buchungscode; eine später stornierte Creator-Rechnung bzw.
 *   widersprochene Gutschrift bekommt im Zeitraum der Stornierung eine Gegenbuchung. Forderungsausfall: Debitor im Haben gegen
 *   Forderungsverluste, samt Berichtigung der Umsatzsteuer (§ 16 UStG).
 * Die Standardkonten folgen dem österreichischen Einheitskontenrahmen (Klasse 4 Erlöse, 5 Fremdleistungen, 7 Forderungsverluste)
 * mit Sammelkonten 200000/330000; alles je Agentur einstellbar. Verzugskosten (Kostennoten mit Mahnspesen, Verzugspauschale,
 * Verzugszinsen; Steuerfall NOT_TAXABLE_DAMAGES) sind kein Umsatzerlös und buchen auf ein eigenes Konto (revenue_late_costs).
 * K-399: Die Verzugszinsen eines solchen Belegs sind Finanzertrag („sonstige Zinsen und ähnliche Erträge“, § 231 Abs. 2 Z 10 UGB,
 * EKR-Klasse 8) und buchen getrennt auf revenue_late_interest (Vorschlag 8100), Mahnspesen und Pauschale bleiben auf 4860.
 * K-397: Euro-Anteile symmetrisch gerundet (roundHalfAway), damit ein Storno spiegelgleich ist.
 */
import { csvFormulaGuard } from "./csv.js";
import { datevBooks, datevEurAmounts, roundHalfAway, splitLateCosts, type DatevInvoice } from "./datev.js";
import { taxModeFromDb } from "./tax.js";
import type { Cents } from "./types.js";

export type BmdAccountKey =
  | "debtor" | "creditor"
  | "revenue_20" | "revenue_reduced" | "revenue_eu_rc" | "revenue_abroad" | "revenue_small_business" | "revenue_other"
  | "revenue_late_costs" | "revenue_late_interest"
  | "expense" | "expense_eu_rc" | "expense_abroad"
  | "bad_debt";
/** Steuerfälle mit eigenem Steuercode (Personenkonten haben keinen) */
export type BmdTaxKey = Exclude<BmdAccountKey, "debtor" | "creditor">;

/**
 * Verzugskosten (Mahnspesen, Pauschale für Betreibungskosten § 458 UGB, Verzugszinsen) als nicht steuerbarer Schadenersatz: eigenes Konto aus
 * der EKR-Gruppe 48 „Übrige betriebliche Erträge“ statt der Umsatzerlöse (4090). Vorschlag; mit der Kanzlei abstimmen.
 */
export const BMD_DEFAULT_LATE_COSTS_ACCOUNT = 4860;
/**
 * K-399: Verzugszinsen (§ 456 UGB, § 1333 ABGB) sind kein betrieblicher Ertrag der Gruppe 48, sondern Finanzertrag: EKR-Gruppe 81
 * „Sonstige Zinsen und ähnliche Erträge“ (GuV-Posten § 231 Abs. 2 Z 10 UGB). Vorschlag 8100; mit der Kanzlei abstimmen.
 */
export const BMD_DEFAULT_LATE_INTEREST_ACCOUNT = 8100;

export const BMD_DEFAULT_ACCOUNTS: Readonly<Record<BmdAccountKey, number>> = {
  debtor: 200000, creditor: 330000,
  revenue_20: 4000, revenue_reduced: 4010, revenue_eu_rc: 4110, revenue_abroad: 4120, revenue_small_business: 4030, revenue_other: 4090,
  revenue_late_costs: BMD_DEFAULT_LATE_COSTS_ACCOUNT, revenue_late_interest: BMD_DEFAULT_LATE_INTEREST_ACCOUNT,
  expense: 5800, expense_eu_rc: 5810, expense_abroad: 5820,
  bad_debt: 7800,
};
/**
 * Vorschläge für den BMD-Steuercode je Steuerfall (siehe Kopfkommentar). Gilt immer dann, wenn die Agentur für den Fall keinen
 * eigenen Code gepflegt hat; „0“ heißt ausdrücklich „ohne Steuer“ statt eines leeren Felds.
 */
export const BMD_DEFAULT_TAX_CODES: Readonly<Record<BmdTaxKey, string>> = {
  // Inland 20 % bzw. 10/13 %: normale Umsatzsteuer
  revenue_20: "1", revenue_reduced: "1",
  // Sonstige Leistung an EU-Unternehmer, Steuerschuld geht über (§ 3a Abs. 6: nicht steuerbar, nicht in der UVA, nur Zusammenfassende Meldung)
  revenue_eu_rc: "77",
  // Drittland, Leistungsort Ausland: nicht steuerbar, keine Kennzahl
  revenue_abroad: "0",
  // Kleinunternehmer § 6 Abs. 1 Z 27: steuerfrei ohne Vorsteuerabzug (KZ 000/016 über das Sachkonto)
  revenue_small_business: "0",
  // Inland mit anderem Satz bzw. manueller Steuerfall mit ausgewiesener Steuer
  revenue_other: "1",
  // Verzugskosten: nicht steuerbarer Schadenersatz
  revenue_late_costs: "0",
  // K-399: Verzugszinsen: nicht steuerbarer Schadenersatz, ohne Steuer
  revenue_late_interest: "0",
  // Vorsteuer aus Creator-Rechnungen; Reverse Charge beim Leistungsbezug (§ 19 Abs. 1 UStG) aus EU und Drittland
  expense: "1", expense_eu_rc: "19", expense_abroad: "19",
  bad_debt: "1",
};
/** Steuerfälle, bei denen der Code nur mit ausgewiesener Steuer gilt (sonst wird keine Steuer gebucht, Feld leer) */
const BMD_TAXED_KEYS: ReadonlySet<BmdTaxKey> = new Set<BmdTaxKey>(["revenue_20", "revenue_reduced", "revenue_other", "expense", "bad_debt"]);

/** Codetabelle der Agentur über den Vorschlägen; leer gespeicherte oder fehlende Codes gelten als nicht gepflegt */
export function bmdTaxCodes(taxCodes: BmdConfig["taxCodes"]): Record<BmdTaxKey, string> {
  const out = { ...BMD_DEFAULT_TAX_CODES } as Record<BmdTaxKey, string>;
  for (const [k, v] of Object.entries(taxCodes ?? {})) {
    if (k in out && typeof v === "string" && v.trim() !== "") out[k as BmdTaxKey] = v.trim();
  }
  return out;
}

export interface BmdConfig {
  accounts?: Partial<Record<BmdAccountKey, number>>;
  taxCodes?: Partial<Record<BmdTaxKey, string>>;
  /** Buchungssymbole; Standard AR, ER, SO */
  symbols?: { outgoing?: string; incoming?: string; other?: string };
  /** Vorzeichen der Steuer: Standard „opposite“ (Gegenseite), sonst wie betrag */
  taxSign?: "opposite" | "same";
  /** Agentur ist Kleinunternehmer: keine Vorsteuer aus Creator-Rechnungen */
  smallBusiness?: boolean;
}

export const BMD_COLUMNS = ["satzart", "konto", "gkonto", "belegnr", "extbelegnr", "belegdatum", "buchsymbol", "buchcode", "prozent", "steuercode", "betrag", "steuer", "text", "zziel"] as const;

export interface BmdProblem { code: "NO_ROWS" | "NO_EUR_AMOUNT" | "ACCOUNT" | "TAX_CODE" | "SYMBOL"; number?: string; key?: string }

export function checkBmdConfig(c: BmdConfig): BmdProblem[] {
  const out: BmdProblem[] = [];
  for (const [k, v] of Object.entries(c.accounts ?? {})) if (!Number.isInteger(v) || (v as number) < 1 || (v as number) > 999_999_999) out.push({ code: "ACCOUNT", key: k });
  for (const [k, v] of Object.entries(c.taxCodes ?? {})) if (typeof v !== "string" || !/^\d{0,2}$/.test(v)) out.push({ code: "TAX_CODE", key: k });
  for (const [k, v] of Object.entries(c.symbols ?? {})) if (v !== undefined && !/^[A-Z]{1,3}$/.test(v)) out.push({ code: "SYMBOL", key: k });
  return out;
}

/** Steuerfall einer Rechnung für Konto und Steuercode */
export function bmdCaseFor(inv: Pick<DatevInvoice, "kind" | "taxMode" | "ratePercent">): BmdTaxKey {
  const mode = taxModeFromDb(inv.taxMode);
  const rate = inv.ratePercent ?? 0;
  if (inv.kind === "talent_to_agency") {
    if (mode === "REVERSE_CHARGE_EU") return "expense_eu_rc";
    if (mode === "REVERSE_CHARGE_DOMESTIC" || mode === "NOT_TAXABLE_ABROAD") return "expense_abroad";
    return "expense";
  }
  switch (mode) {
    case "DOMESTIC": return Math.abs(rate - 20) < 0.05 ? "revenue_20" : rate > 0 && rate < 20 ? "revenue_reduced" : "revenue_other";
    case "REVERSE_CHARGE_EU": return "revenue_eu_rc";
    case "NOT_TAXABLE_ABROAD": return "revenue_abroad";
    case "SMALL_BUSINESS": return "revenue_small_business";
    case "NOT_TAXABLE_DAMAGES": return "revenue_late_costs";
    default: return "revenue_other";
  }
}

const amount = (c: Cents) => `${c < 0 ? "-" : ""}${(Math.abs(c) / 100).toFixed(2).replace(".", ",")}`;
const ddmmyyyy = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
/** Textfeld: ohne Trenner und Zeilenumbrüche, gegen Formeln geschützt (führendes '), höchstens max Zeichen */
const field = (s: string, max: number) => csvFormulaGuard(s.replace(/[\r\n;"]+/g, " ").replace(/\s+/g, " ").trim()).slice(0, max);
/** belegnr: die Ziffern der Rechnungsnummer (höchstens die letzten 9), sonst leer */
export function bmdBelegnr(number: string): string {
  const d = number.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  return d.slice(-9);
}

/**
 * Buchungen als CSV-Text für BMD (Zeilenende CRLF). Rechnungen ohne Euro-Betrag (fremde Währung ohne Kurs) werden gemeldet und
 * ausgelassen; Rechnungen, die die Agentur nicht bucht, zählen als übersprungen.
 */
export function buildBmdBuchungen(input: {
  config: BmdConfig;
  invoices: readonly DatevInvoice[];
  from: string;
  to: string;
}): { text: string; rows: number; skipped: number; problems: BmdProblem[] } {
  const c = input.config;
  const problems = checkBmdConfig(c);
  const acc = { ...BMD_DEFAULT_ACCOUNTS, ...(c.accounts ?? {}) } as Record<BmdAccountKey, number>;
  const codes = bmdTaxCodes(c.taxCodes);
  const sym = { outgoing: c.symbols?.outgoing || "AR", incoming: c.symbols?.incoming || "ER", other: c.symbols?.other || "SO" };
  const taxFactor = c.taxSign === "same" ? 1 : -1;
  const lines: string[] = [BMD_COLUMNS.join(";")];
  let skipped = 0;
  const inRange = (d: string | null | undefined) => !!d && d >= input.from && d <= input.to;
  for (const inv of input.invoices) {
    if (!datevBooks(inv)) { skipped += 1; continue; }
    const incoming = inv.kind === "talent_to_agency";
    const foreign = inv.currency !== "EUR";
    const eur = datevEurAmounts(inv);
    if (foreign && !eur) { problems.push({ code: "NO_EUR_AMOUNT", number: inv.number }); continue; }
    const toEur = (cents: number) => (eur && inv.grossCents !== 0 ? roundHalfAway((cents * eur.grossEurCents) / inv.grossCents) : cents);
    const key = bmdCaseFor(inv);
    const mode = taxModeFromDb(inv.taxMode);
    // Vorsteuer nur mit ausgewiesener Steuer und nicht als Kleinunternehmer-Agentur; Umsatzsteuer nur im Inlandsfall
    const withTax = incoming ? mode === "DOMESTIC" && inv.taxCents !== 0 && !c.smallBusiness : mode === "DOMESTIC" && inv.taxCents !== 0;
    const rate = withTax ? inv.ratePercent ?? 0 : 0;
    const zziel = inv.dueDate && inv.issueDate ? Math.max(0, Math.round((Date.parse(inv.dueDate) - Date.parse(inv.issueDate)) / 86_400_000)) : null;
    const foreignText = foreign ? ` (${inv.currency} ${amount(inv.grossCents)})` : "";
    const row = (o: { date: string; konto: number; gkonto: number; symbol: string; soll: boolean; gross: number; tax: number; rate: number; code: string; text: string }) => {
      const sign = o.soll ? 1 : -1;
      const f = [
        "0", String(o.konto), String(o.gkonto), bmdBelegnr(inv.number), field(inv.number, 20), ddmmyyyy(o.date), o.symbol, o.soll ? "1" : "2",
        o.rate ? String(o.rate).replace(".", ",") : "0", o.code, amount(sign * Math.abs(o.gross)), amount(o.tax === 0 ? 0 : taxFactor * sign * Math.abs(o.tax)),
        field(o.text, 50), zziel == null ? "" : String(zziel),
      ];
      lines.push(f.join(";"));
    };
    const label = `${inv.selfBilled ? "Gutschrift " : ""}${inv.counterparty}`;
    // K-308: Steuer in Euro wie auf dem PDF, wenn eingefroren
    const gross = eur ? eur.grossEurCents : inv.grossCents, tax = withTax ? (eur ? eur.taxEurCents : inv.taxCents) : 0;
    const negative = inv.grossCents < 0;
    // Ausgangsrechnung: Debitor im Soll; Eingangsrechnung/Gutschrift: Kreditor im Haben; Storno dreht die Seite
    const baseSoll = !incoming;
    const soll = negative ? !baseSoll : baseSoll;
    // Steuercode des Steuerfalls; Inlandsfälle nur mit gebuchter Steuer, alle übrigen Fälle immer (K-252)
    const code = BMD_TAXED_KEYS.has(key) ? (withTax ? codes[key] : "") : codes[key];
    const common = { konto: incoming ? acc.creditor : acc.debtor, gkonto: acc[key], symbol: incoming ? sym.incoming : sym.outgoing, rate, code };
    // K-399: Beleg über Verzugskosten je Position: Zinsen auf das Zinsenkonto, Mahnspesen und Pauschale auf das Verzugskostenkonto
    const split = key === "revenue_late_costs" ? splitLateCosts(inv.grossCents, eur ? eur.grossEurCents : inv.grossCents, inv.lateInterestCents) : null;
    const parts: { gross: number; tax: number; gkonto: number; code: string; text: string }[] = split
      ? [
          { gross: split.interest.eurCents ?? split.interest.cents, tax: 0, gkonto: acc.revenue_late_interest, code: codes.revenue_late_interest, text: `Verzugszinsen ${label}${foreignText}` },
          ...(split.rest.cents !== 0 ? [{ gross: split.rest.eurCents ?? split.rest.cents, tax: 0, gkonto: acc[key], code, text: `Verzugskosten ${label}${foreignText}` }] : []),
        ]
      : [{ gross, tax, gkonto: acc[key], code, text: `${label}${foreignText}` }];
    if (inv.issuedInRange !== false) for (const p of parts) row({ ...common, ...p, date: inv.issueDate, soll });
    // Gegenbuchung einer später stornierten Creator-Rechnung bzw. widersprochenen Gutschrift
    if (incoming && inv.status === "cancelled" && inRange(inv.cancelledOn)) {
      for (const p of parts) row({ ...common, ...p, date: inv.cancelledOn!, soll: !soll, text: `Storno ${p.text}` });
    }
    // Forderungsausfall: Debitor im Haben gegen Forderungsverluste, Umsatzsteuer anteilig berichtigt
    if (!incoming && inRange(inv.badDebtOn) && (inv.badDebtCents ?? 0) > 0) {
      const bad = toEur(inv.badDebtCents!);
      const badTax = withTax && rate > 0 ? roundHalfAway((bad * rate) / (100 + rate)) : 0;
      row({ konto: acc.debtor, gkonto: acc.bad_debt, symbol: sym.other, soll: false, gross: bad, tax: badTax, rate: badTax ? rate : 0,
        // ohne Umsatzsteuer (EU, Drittland, Kleinunternehmer, Verzugskosten) behält die Ausbuchung den Code ihres Steuerfalls
        code: badTax ? codes.bad_debt : BMD_TAXED_KEYS.has(key) ? "" : codes[key], date: inv.badDebtOn!, text: `Forderungsausfall ${inv.counterparty}` });
    }
  }
  if (lines.length === 1) problems.push({ code: "NO_ROWS" });
  return { text: `${lines.join("\r\n")}\r\n`, rows: lines.length - 1, skipped, problems };
}
